import React, { useEffect, useState } from "react";
import api from "../api";
import "./LoanOfficers.css";

const DIVISIONS = ["retail", "retention"];

function LOEditModal({ lo, onClose, onSave }) {
  const [division, setDivision] = useState(lo.division || "retail");
  const [tier1, setTier1] = useState(lo.retention_tier1_bps);
  const [tier2, setTier2] = useState(lo.retention_tier2_bps);
  const [tier3, setTier3] = useState(lo.retention_tier3_bps);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [error, setError] = useState("");

  const isCustom = lo.retention_plan_is_custom;
  const defaultsChanged =
    tier1 !== lo.retention_tier1_bps ||
    tier2 !== lo.retention_tier2_bps ||
    tier3 !== lo.retention_tier3_bps;
  const divisionChanged = division !== lo.division;

  const handleSave = async () => {
    if (tier1 < 0 || tier2 < 0 || tier3 < 0) {
      setError("BPS values must be 0 or greater");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const updates = [];
      if (divisionChanged) {
        updates.push(api.put(`/users/${lo.id}`, { division }));
      }
      // Always save the retention plan if the division is retention, or if there are tier changes
      if (division === "retention" || defaultsChanged) {
        updates.push(api.put(`/users/retention-plan/${lo.id}`, {
          tier1_bps: Number(tier1),
          tier2_bps: Number(tier2),
          tier3_bps: Number(tier3),
        }));
      }
      await Promise.all(updates);
      onSave();
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setResetting(true);
    setError("");
    try {
      await api.delete(`/users/retention-plan/${lo.id}`);
      onSave();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to reset plan");
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-card lo-modal-card">
        <div className="modal-header">
          <div>
            <h2>{lo.full_name}</h2>
            <div className="modal-subheader">{lo.email}</div>
          </div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="modal-form">
          {error && <div className="modal-error">{error}</div>}

          {/* Division */}
          <div className="form-section">
            <div className="form-section-title">Division</div>
            <div className="division-toggle">
              {DIVISIONS.map(d => (
                <button
                  key={d}
                  type="button"
                  className={`div-btn ${division === d ? "selected" : ""}`}
                  onClick={() => setDivision(d)}
                >
                  {d.charAt(0).toUpperCase() + d.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Retention plan tiers */}
          {division === "retention" && (
            <div className="form-section">
              <div className="form-section-title">
                Retention Comp Plan
                {isCustom && <span className="custom-badge">Custom</span>}
              </div>
              <p className="form-section-desc">
                Monthly funded loan count determines the BPS rate paid on each loan.
              </p>
              <div className="tier-grid">
                <div className="tier-row">
                  <div className="tier-label">
                    <span className="tier-name">Tier 1</span>
                    <span className="tier-range">0 – 4 loans/mo</span>
                  </div>
                  <div className="tier-input-wrap">
                    <input
                      type="number"
                      min="0"
                      value={tier1}
                      onChange={e => setTier1(e.target.value)}
                      className="tier-input"
                    />
                    <span className="tier-unit">bps</span>
                  </div>
                </div>
                <div className="tier-row">
                  <div className="tier-label">
                    <span className="tier-name">Tier 2</span>
                    <span className="tier-range">5 – 7 loans/mo</span>
                  </div>
                  <div className="tier-input-wrap">
                    <input
                      type="number"
                      min="0"
                      value={tier2}
                      onChange={e => setTier2(e.target.value)}
                      className="tier-input"
                    />
                    <span className="tier-unit">bps</span>
                  </div>
                </div>
                <div className="tier-row">
                  <div className="tier-label">
                    <span className="tier-name">Tier 3</span>
                    <span className="tier-range">8+ loans/mo</span>
                  </div>
                  <div className="tier-input-wrap">
                    <input
                      type="number"
                      min="0"
                      value={tier3}
                      onChange={e => setTier3(e.target.value)}
                      className="tier-input"
                    />
                    <span className="tier-unit">bps</span>
                  </div>
                </div>
              </div>

              {isCustom && (
                <div className="reset-row">
                  <span className="reset-desc">This LO has a custom plan. Reset to company defaults (15 / 30 / 40 bps).</span>
                  <button
                    type="button"
                    className="btn-danger-outline"
                    onClick={handleReset}
                    disabled={resetting}
                  >
                    {resetting ? "Resetting..." : "Reset to Defaults"}
                  </button>
                </div>
              )}
            </div>
          )}

          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button
              type="button"
              className="btn-primary"
              onClick={handleSave}
              disabled={saving || (!divisionChanged && division !== "retention" && !defaultsChanged)}
            >
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function LoanOfficers() {
  const [los, setLos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [divFilter, setDivFilter] = useState("all");
  const [modal, setModal] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/users/loan-officers")
      .then(r => setLos(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const filtered = los.filter(lo => {
    const matchSearch =
      lo.full_name?.toLowerCase().includes(search.toLowerCase()) ||
      lo.email?.toLowerCase().includes(search.toLowerCase()) ||
      lo.nmls_number?.toLowerCase().includes(search.toLowerCase());
    const matchDiv = divFilter === "all" || lo.division === divFilter;
    return matchSearch && matchDiv;
  });

  const fmtBps = bps => `${bps} bps`;

  if (loading) return <div className="loading">Loading loan officers...</div>;

  return (
    <div className="lo-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Loan Officers</h1>
          <p className="page-subtitle">{los.length} loan officers</p>
        </div>
      </div>

      <div className="table-card">
        <div className="table-toolbar">
          <input
            className="search-input"
            placeholder="Search by name, email, or NMLS..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <div className="div-filter-tabs">
            {["all", "retail", "retention"].map(d => (
              <button
                key={d}
                className={`div-filter-btn ${divFilter === d ? "active" : ""}`}
                onClick={() => setDivFilter(d)}
              >
                {d.charAt(0).toUpperCase() + d.slice(1)}
              </button>
            ))}
          </div>
          <span className="result-count">{filtered.length} results</span>
        </div>

        <table className="data-table">
          <thead>
            <tr>
              <th>Loan Officer</th>
              <th>NMLS</th>
              <th>Division</th>
              <th>Comp Plan / Tier</th>
              <th>This Month</th>
              <th>Manager</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={8} className="empty-row">No loan officers found</td></tr>
            )}
            {filtered.map(lo => (
              <tr key={lo.id}>
                <td>
                  <div className="user-cell">
                    <div className="user-cell-avatar">{lo.full_name?.[0]?.toUpperCase()}</div>
                    <div>
                      <div className="user-cell-name">{lo.full_name}</div>
                      <div className="lo-email">{lo.email}</div>
                    </div>
                  </div>
                </td>
                <td className="mono text-muted">{lo.nmls_number || "—"}</td>
                <td>
                  <span className={`division-badge div-${lo.division}`}>
                    {lo.division === "retention" ? "Retention" : "Retail"}
                  </span>
                </td>
                <td>
                  {lo.division === "retention" ? (
                    <div className="comp-plan-cell">
                      <span className="comp-tier-row">
                        <span className="comp-tier-label">T1</span>
                        <span className="comp-tier-val">{fmtBps(lo.retention_tier1_bps)}</span>
                      </span>
                      <span className="comp-tier-row">
                        <span className="comp-tier-label">T2</span>
                        <span className="comp-tier-val">{fmtBps(lo.retention_tier2_bps)}</span>
                      </span>
                      <span className="comp-tier-row">
                        <span className="comp-tier-label">T3</span>
                        <span className="comp-tier-val">{fmtBps(lo.retention_tier3_bps)}</span>
                      </span>
                      {lo.retention_plan_is_custom && (
                        <span className="custom-plan-tag">Custom</span>
                      )}
                    </div>
                  ) : (
                    <span className={`badge badge-tier-${lo.current_tier || "low"}`}>
                      {lo.current_tier
                        ? lo.current_tier.charAt(0).toUpperCase() + lo.current_tier.slice(1)
                        : "Low"}
                    </span>
                  )}
                </td>
                <td>
                  {lo.division === "retention" ? (
                    <div className="month-stats">
                      <span className="month-loans">{lo.current_month_loans} loans</span>
                      <span className="month-bps">{fmtBps(lo.current_month_bps)}</span>
                    </div>
                  ) : (
                    <span className="text-muted">—</span>
                  )}
                </td>
                <td className="text-secondary">{lo.manager_name || "—"}</td>
                <td>
                  <span className={`badge ${lo.is_active ? "badge-active" : "badge-cancelled"}`}>
                    {lo.is_active ? "Active" : "Inactive"}
                  </span>
                </td>
                <td>
                  <button
                    className="btn-edit"
                    onClick={() => setModal(lo)}
                  >
                    ✎ Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <LOEditModal
          lo={modal}
          onClose={() => setModal(null)}
          onSave={() => { setModal(null); load(); }}
        />
      )}
    </div>
  );
}
